import { useCallback, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
// clearAIParametersCacheはコメントアウトしたため、インポートも無効化（軽量化のため）
// import { clearAIParametersCache } from "../utils/api";
import { useModal } from "../hooks/useModal";
import { useToastContext } from "../App";
import { useAuth } from "../features/accounts/contexts/AuthContext";
import { useAIChatState } from "../features/aiChat/hooks/useAIChatState";
import { useWebSocketConnection } from "../features/aiChat/hooks/useWebSocketConnection";
import { clearResetConversationMark } from "../features/aiChat/utils/aiChatUtils";
import ChatMessages from "../features/aiChat/components/ChatMessages";
import ChatInputArea from "../features/aiChat/components/ChatInputArea";
import ChatModals from "../features/aiChat/components/ChatModals";
import ChatHeader from "../features/aiChat/components/ChatHeader";
import AIConfigStatus from "../features/aiChat/components/AIConfigStatus";
import { pageShell } from "../styles/pageShell";

export default function AiChatPage() {
    const { projectId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user: userFromAuth } = useAuth();
    const { showToast } = useToastContext();
    const messagesBoxRef = useRef(null);

    const shouldResetFromNav = location.state?.resetConversation === true;

    // storage の useEffect より前に実行し、孤立フラグで履歴が消えるのを防ぐ
    useLayoutEffect(() => {
        if (!shouldResetFromNav) {
            clearResetConversationMark();
        }
    }, [shouldResetFromNav]);

    // WebSocket接続管理
    const { isConnected, waitForResult, reconnect } = useWebSocketConnection();

    // チャット状態管理
    const chatState = useAIChatState(projectId, userFromAuth, isConnected, waitForResult, reconnect);
    const {
        messages,
        input,
        setInput,
        loading,
        error,
        purging,
        showLoadingIndicator,
        hydrating,
        openingFetching,
        pendingInput,
        setPendingInput,
        editingMessage,
        editContent,
        setEditContent,
        aiConfig,
        checkingAiConfig,
        aiConfigError,
        MAX_TURNS,
        hasTurnLimit,
        isTurnLimitReached,
        sendMessage: sendMessageFromHook,
        handleEditMessage,
        handleConfirmEdit,
        handleNewConversation,
        textareaRef,
        formRef,
    } = chatState;

    // handleNewConversation は毎レンダーで参照が変わり得るため ref 経由で呼ぶ
    // （依存に入れると resetConversation 中に無限リセットループになる）
    const handleNewConversationRef = useRef(handleNewConversation);
    handleNewConversationRef.current = handleNewConversation;

    // モーダル管理
    const { isOpen: isConfirmOpen, openModal: openConfirm, closeModal: closeConfirm } = useModal(false);
    const { isOpen: isSendConfirmOpen, openModal: openSendConfirm, closeModal: closeSendConfirm } = useModal(false);
    const { isOpen: isEditOpen, openModal: openEdit, closeModal: closeEdit } = useModal(false);

    // エラーが設定されたときにToastを表示
    useEffect(() => {
        if (error) {
            showToast(error, "error");
        }
    }, [error, showToast]);

    // スクロールを最下部に
    const scrollToBottom = useCallback(() => {
        const el = messagesBoxRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, loading, showLoadingIndicator, scrollToBottom]);

    // ページロード時にパラメータキャッシュをクリア
    // コメントアウト（軽量化のため、キャッシュを活用）
    // useEffect(() => {
    //     if (projectId) {
    //         clearAIParametersCache(projectId);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    // 遷移 state で会話履歴をリセット（地図の「AI会話」など）
    // handleNewConversation を deps に入れない（参照変化でリセットがループするため）
    // フラグ自体はピン押下時の markResetConversation（sessionStorage）が正
    useEffect(() => {
        if (!shouldResetFromNav) return;
        let cancelled = false;
        (async () => {
            await handleNewConversationRef.current();
            if (cancelled) return;
            navigate(location.pathname, { replace: true, state: {} });
        })();
        return () => {
            cancelled = true;
        };
    }, [shouldResetFromNav, location.pathname, navigate]);

    // メッセージ編集の開始（モーダルを開く）
    const handleEditMessageWithModal = useCallback((message) => {
        handleEditMessage(message);
        openEdit();
    }, [handleEditMessage, openEdit]);

    // 編集の確定と再送信（モーダルを閉じる）
    const handleConfirmEditWithModal = useCallback(async () => {
        await handleConfirmEdit();
        closeEdit();
    }, [handleConfirmEdit, closeEdit]);

    // 送信処理（確認ダイアログあり）
    const sendMessage = useCallback(async () => {
        await sendMessageFromHook(null, pendingInput);
    }, [sendMessageFromHook, pendingInput]);

    // フォーム送信
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (!input.trim() || loading || purging || openingFetching || isTurnLimitReached) return;

        // ターン数のチェック（disaster_tankyu のときのみ）
        if (hasTurnLimit) {
            const userMessages = messages.filter(m => m.role === "user");
            const nextTurn = userMessages.length + 1;
            if (nextTurn > MAX_TURNS) {
                chatState.setError(`最大${MAX_TURNS}ターンまで送信できます。これ以上の会話はできません。`);
                return;
            }
        }

        // 確認ダイアログを表示
        setPendingInput(input);
        openSendConfirm();
    }, [input, loading, purging, openingFetching, isTurnLimitReached, hasTurnLimit, messages, MAX_TURNS, chatState, setPendingInput, openSendConfirm]);

    // 新しい会話を開始（モーダルを閉じる）
    const handleNewConversationWithModal = useCallback(async () => {
        closeConfirm();
        await handleNewConversation();
    }, [closeConfirm, handleNewConversation]);

    // パラメータ表示用のテキストを生成（function_model_id に応じて表示を分岐）
    const parametersText = useMemo(() => {
        if (!aiConfig) return null;
        const parts = [];
        if (aiConfig.default_age) {
            parts.push(`年齢: ${aiConfig.default_age}`);
        }
        const modelId = aiConfig.function_model_id || (aiConfig.model_type === 'regional_learning' ? 'furusato' : 'disaster_tankyu');
        if (modelId === 'disaster_tankyu') {
            if (aiConfig.default_disaster_type) {
                parts.push(`災害種別: ${aiConfig.default_disaster_type}`);
            }
            if (aiConfig.default_tsunami) {
                parts.push(`津波: ${aiConfig.default_tsunami}`);
            }
            // 防災シミュレーションは8ターン固定
            parts.push('最大ターン: 8ターン');
        } else if (modelId === 'disaster_bigbrother') {
            if (aiConfig.default_disaster_type) {
                parts.push(`災害種別: ${aiConfig.default_disaster_type}`);
            }
        }
        return parts.length > 0 ? parts.join(' / ') : null;
    }, [aiConfig]);

    // ターン表示を表示するかどうか（disaster_tankyu のみ。BigBrother / ふるさとはターン概念なし）
    const showTurn = useMemo(() => {
        return aiConfig?.function_model_id === 'disaster_tankyu';
    }, [aiConfig]);

    // AI設定エラーがある場合の表示
    if (checkingAiConfig || aiConfigError) {
        return (
            <AIConfigStatus
                checkingAiConfig={checkingAiConfig}
                aiConfigError={aiConfigError}
            />
        );
    }

    return (
        <div className={pageShell.container}>
            <ChatHeader
                parametersText={parametersText}
                onNewConversation={openConfirm}
            />
            <div className={pageShell.card}>
                <ChatMessages
                    messages={messages}
                    loading={loading}
                    purging={purging}
                    showLoadingIndicator={showLoadingIndicator}
                    showTurn={showTurn}
                    onEdit={handleEditMessageWithModal}
                    messagesBoxRef={messagesBoxRef}
                    hydrating={hydrating}
                    openingFetching={openingFetching}
                />
            </div>

            <ChatInputArea
                input={input}
                setInput={setInput}
                onSubmit={handleSubmit}
                loading={loading}
                purging={purging}
                isTurnLimitReached={isTurnLimitReached}
                maxTurns={MAX_TURNS}
                textareaRef={textareaRef}
                formRef={formRef}
                isWsConnected={isConnected}
                openingFetching={openingFetching}
            />

            <ChatModals
                isConfirmOpen={isConfirmOpen}
                closeConfirm={closeConfirm}
                handleNewConversation={handleNewConversationWithModal}
                purging={purging}
                isSendConfirmOpen={isSendConfirmOpen}
                closeSendConfirm={closeSendConfirm}
                sendMessage={sendMessage}
                pendingInput={pendingInput}
                loading={loading}
                isEditOpen={isEditOpen}
                closeEdit={closeEdit}
                handleConfirmEdit={handleConfirmEditWithModal}
                editContent={editContent}
                setEditContent={setEditContent}
                editingMessage={editingMessage}
                showTurn={showTurn}
            />
        </div>
    );
}
